<template>
  <div class="profile-section secondary" ref="profileSection" id="profileSection">
    <!-- start of header -->
    <div class="section-header">
      <v-layout row>
        <v-flex lg6>
          <div class="section-name">{{ layoutSetting.section.caption }}</div>
          <arb-review-status-icon
            v-if="layoutSetting.section.isVerify"
            :keys="layoutSetting.section.fieldKey"
            :reviewData="extension.reviewStatusData"
          ></arb-review-status-icon>
          <arb-bulb-tooltips
            v-if="assist.tool.isNotEmpty(layoutSetting.section.speechCraftCode)"
            class="section-name pl-1"
            :position="layoutSetting.section.tooltipPosition"
            :speechCraftCode="layoutSetting.section.speechCraftCode"
            :toolTipData="toolTipData"
          ></arb-bulb-tooltips>
        </v-flex>
        <v-flex lg6>
          <div class="section-action">
            <arb-event-tooltips
              v-if="isShowElement(authorization.privileges, layoutSetting.section.code, assist.dataType.PrivilegeType.WRITABLE) && isOperational() && isEdit()"
              :icon="'notes'"
              :toolTipData="'EDIT'"
              @click="showProfileSectionDialog('EDIT')"
            ></arb-event-tooltips>
            <arb-event-tooltips
              v-if="isShowElement(authorization.privileges, layoutSetting.section.code, assist.dataType.PrivilegeType.EXECUTABLE) && isOperational() && isAudit()"
              :icon="'rule'"
              :color="'red'"
              :toolTipData="'AUDIT'"
              @click="showProfileSectionDialog('AUDIT')"
            ></arb-event-tooltips>
          </div>
        </v-flex>
      </v-layout>
    </div>
    <!-- start of header //:~ -->

    <!-- start of title -->
    <div class="section-banner" v-if="layoutSetting.title && !((loan.loanType === assist.dataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value) && (layoutSetting.section.name === 'payment-option'))" :style="layoutSetting.section.name === 'payment-option' ? 'width: 93%' : ''">
      <v-layout row v-if="layoutSetting.title instanceof Array">
        <v-flex v-for="(title, index) in layoutSetting.title" :key="index" :class="'lg' + 12 / layoutSetting.title.length">
          <label v-if="showTitle(assist.dataType.LoanTypeEnum.INSTALLMENT_LOAN.value)">{{ title.label.installmentLoan }}</label>
          <label v-if="showTitle(assist.dataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value)">{{ title.label.multipleAdvanceLoan }}</label>
          <label v-if="typeof title.label === 'string'">{{ title.label }}</label>
          <strong :class="title.class">
            {{ assist.tool.fieldRender(dataSource.sectionData, title.template, title.format) }}
          </strong>
          <arb-event-tooltips
            v-if="isShowElement(authorization.privileges, title.code, assist.dataType.PrivilegeType.EXECUTABLE)"
            :className="`material-icons text-warning ${title.icon}`"
            :toolTipData="title.tooltip"
            @click="triggerAmount(title.action)"
          ></arb-event-tooltips>
        </v-flex>
      </v-layout>
      <div v-else>
        <v-layout row>
          <v-flex lg6>
            <strong class="text-inverse">
              {{ assist.tool.fieldRender(dataSource.sectionData, layoutSetting.title.template, null, null, layoutSetting.title.case) }}
            </strong>
            <arb-review-status-icon
              :keys="layoutSetting.title.fieldKey"
              :reviewData="extension.reviewStatusData"
            ></arb-review-status-icon>
          </v-flex>
          <v-flex lg6>
            <div v-if="layoutSetting.section.name === 'personal'" class="section-action">
              <template v-for="(optIn, index) in optInArray">
                <arb-event-tooltips
                  v-if="optIn.display"
                  :key="index"
                  :className="optIn.style"
                  :toolTipData="optIn.tooltip"
                ></arb-event-tooltips>
              </template>
            </div>
          </v-flex>
        </v-layout>
      </div>
    </div>
    <!-- end of title //:~ -->

    <!-- start of form section -->
    <section-form
      v-if="layoutSetting.section.type === 'form'"
      :style="(layoutSetting.section.name === 'payment-option' || 'collection-summary') ? 'width: 93%' : ''"
      :sectionData="dataSource.sectionData"
      :reviewData="extension.reviewStatusData"
      :layoutSetting="layoutSetting"
      :loan="loan"
      :privileges="authorization.privileges"
      :currentLockedId="currentLockedId"
      :operator="operator"
      :toolTipData="toolTipData"
    ></section-form>
    <!-- end of form section //:~ -->

    <!-- start of question -->
    <section-question
      v-if="layoutSetting.questionGroup"
      :questionTemplate="dataSource.questionTemplateData"
      :questionAnswerData="extension.questionAnswerData"
    ></section-question>
    <!-- end of question //:~ -->

    <!-- start of table -->
    <arb-simple-table
      class="section-table"
      v-if="layoutSetting.section.type === 'table'"
      :desserts="dataSource.tableData"
      :header="layoutSetting.sectionFields"
      :height="180"
      :color="$vuetify.theme.themes.light.secondary"
      fontColor="#002B7F"
    ></arb-simple-table>
    <!-- end of table //:~ -->

    <!-- start of some dialog -->
    <v-dialog v-model="dialog.profileSectionDialog.display" persistent :max-width="layoutSetting.dialogWidth === undefined ? '950px' : layoutSetting.dialogWidth">
      <application-detail-dialog
        v-if="dialog.profileSectionDialog.display"
        :loan="loan"
        :slaveLoanId="slaveLoanId"
        :layoutSetting="layoutSetting"
        :dataSource="dataSource"
        :extension="extension"
        :setting="dialog.profileSectionDialog"
        @sectionUpdated="sectionUpdatedEventHandler"
      ></application-detail-dialog>
    </v-dialog>

    <v-dialog v-model="dialog.paymentPlanDialog.display" persistent max-width="500px">
      <application-detail-generate-payment-plan-dialog
        :loan="loan"
        :dataSource="dataSource"
        :slaveLoanId="slaveLoanId"
        @hideDialog="hidePaymentPlanEventHandler"
      ></application-detail-generate-payment-plan-dialog>
    </v-dialog>
    <!-- end of some dialog //:~ -->
  </div>
</template>

<script>
import { Authentication, Crypto, DataType, Tool, Popup } from '@/util/index'
import { PaymentApi, OriginationApi, CollectionApi, ProcessingApi } from '@/api/pandaria'
import { LIST_INIT_STRUCTURE } from '@/setting/list-header/list-init-structure'
import SectionQuestion from '@/components/panda/common/detail/section/SectionQuestion'
import SectionForm from '@/components/panda/common/detail/section/SectionForm'
import { AnalogData } from '@/api/analogData'
import { _ } from 'vue-underscore'

const tool = new Tool()
const popup = new Popup()
export default {
  name: 'application-detail-profile-section',
  components: { SectionQuestion, SectionForm },
  props: {
    layoutSetting: Object,
    loan: Object,
    authorization: {
      type: Object,
      default: () => {}
    },
    toolTipData: String,
    slaveLoanId: Number,
    operator: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      dialog: {
        profileSectionDialog: {
          action: '',
          display: false
        },
        paymentPlanDialog: {
          display: false
        }
      },
      dataSource: {
        questionTemplateData: AnalogData.QUESTION,
        sectionData: {},
        reviewData: [],
        answerData: [],
        tableData: []
      },
      extension: {
        reviewStatusData: {},
        questionOptionData: {},
        questionAnswerData: {}
      },
      assist: {
        tool: new Tool(),
        dataType: DataType
      },
      score: {
        reviewData: [],
        sectionData: {}
      },
      currentLockedId: -1,
      optInArray: JSON.parse(JSON.stringify(LIST_INIT_STRUCTURE.OPT_IN_ARRAY))
    }
  },
  computed: {
    loanId () {
      return this.loan.id
    }
  },
  watch: {
    loanId (val) {
      this.toLoadSectionData(val)
    },
    slaveLoanId: {
      handler: function (val) {
        this.loadSlaveLoanPaymentOption(val)
      },
      deep: true
    },
    'dataSource.reviewData': {
      handler: function () {
        const sectionName = this.layoutSetting.section.name
        const reviewData = JSON.parse(JSON.stringify(this.dataSource.reviewData))
        if (this.assist.tool.isNotEmpty(reviewData)) {
          this.score.reviewData = reviewData
          this.calculateScore(sectionName)
        }
      },
      deep: true,
      immediate: true
    },
    'dataSource.sectionData': {
      handler: function () {
        const sectionName = this.layoutSetting.section.name
        const sectionData = JSON.parse(JSON.stringify(this.dataSource.sectionData))
        if (this.assist.tool.isNotEmpty(sectionData)) {
          this.score.sectionData = sectionData
          this.calculateScore(sectionName)
        }
      },
      deep: true,
      immediate: true
    }
  },
  destroyed () {
    this.$eventBus.$off('saveApprovedAmount')
    this.$eventBus.$off('loanAmountUpdated')
    this.$eventBus.$off('asyncAprToLoanDetailSection')
    this.$eventBus.$off('clearData')
    this.$eventBus.$off('manualSelectBankRecords')
    this.$eventBus.$off('broadcastLockedId')
    this.$eventBus.$off('broadcastOptIn')
  },
  mounted () {
    // mock
    this.getApplicationReviewDataCallback(this.dataSource.reviewData)
    if (this.layoutSetting.section.name === 'collection-summary') {
      CollectionApi.getSummaryByLoanId({ loanId: 1 }, this.getSummaryByLoanIdCallBack)
    }
    if (this.layoutSetting.section.name === 'payment-option') {
      this.$eventBus.$on('saveApprovedAmount', target => {
        this.dataSource.sectionData.approvedAmount = target.approvedAmount
        this.dataSource.sectionData.totalAmount = target.approvedAmount
        this.dataSource.sectionData.availableAdvanceAmount = this.dataSource.sectionData.loanAmount / 2 - target.approvedAmount
        this.$forceUpdate()
      })
      this.$eventBus.$on('loanAmountUpdated', target => {
        const loanAmount = target.loanAmount
        this.dataSource.sectionData.loanAmount = loanAmount
        if (tool.isNotEmpty(target.approvedAmount)) {
          this.dataSource.sectionData.approvedAmount = target.approvedAmount
        }
        this.dataSource.sectionData.availableAdvanceAmount = loanAmount / 2 - this.dataSource.sectionData.approvedAmount
        const transferredAmount = this.dataSource.sectionData.transferredAmount || 0
        this.dataSource.sectionData.totalAmount = transferredAmount + this.dataSource.sectionData.approvedAmount
        this.$forceUpdate()
      })
      this.$eventBus.$on('asyncAprToLoanDetailSection', target => {
        this.dataSource.sectionData.apr = target.apr
        this.$forceUpdate()
      })
      this.$eventBus.$on('get-clicked-slave-detail-event', target => {
        if (!target.isFirstLoad) {
          // PaymentApi.getLoanDetail({ parentId: this.loanId, loanId: target.loanId }, this.getLoanDetailsBasicCallback)
        }
      })
      this.$eventBus.$on('clearData', target => {
        delete this.dataSource.sectionData.approvedAmount
        delete this.dataSource.sectionData.apr
        delete this.dataSource.sectionData.loanAmount
        delete this.dataSource.sectionData.totalAmount
        delete this.dataSource.sectionData.debitCount
        delete this.dataSource.sectionData.firstDebitDate
        delete this.dataSource.sectionData.lastDebitDate
        delete this.dataSource.sectionData.interestRate
        delete this.dataSource.sectionData.regularAmount
        delete this.dataSource.sectionData.availableAdvanceAmount
        this.$forceUpdate()
      })
      this.$eventBus.$on('clearScheduleData', target => {
        delete this.dataSource.sectionData.apr
        delete this.dataSource.sectionData.debitCount
        delete this.dataSource.sectionData.firstDebitDate
        delete this.dataSource.sectionData.lastDebitDate
        delete this.dataSource.sectionData.interestRate
        delete this.dataSource.sectionData.regularAmount
        this.$forceUpdate()
      })
      this.$eventBus.$on('loanDetailDisplay', target => {
        this.dataSource.sectionData.debitCount = target.debitCount
        this.dataSource.sectionData.firstDebitDate = target.firstDebitDate
        this.dataSource.sectionData.lastDebitDate = target.lastDebitDate
        this.dataSource.sectionData.regularAmount = target.regularAmount
        this.dataSource.sectionData.interestRate = target.interestRate
        this.dataSource.sectionData.apr = target.apr
        this.$forceUpdate()
      })
    }
    if (this.layoutSetting.section.name === 'statement') {
      this.$eventBus.$on('manualSelectBankRecords', target => {
        const $this = this
        for (const item of target.bankRecordsDataFinalData) {
          item.payrollDate = tool.formatDate(item.payrollDate)
        }
        const requestData = { loanId: $this.loan.id, bankRecords: target.bankRecordsDataFinalData }
        OriginationApi.updateLoanBankRecordsData(requestData, function (updateResult) {
          if (tool.isNotEmpty($this.dataSource.reviewData)) {
            for (const item of $this.dataSource.reviewData) {
              if (item.fieldKey === 'statement' && item.reviewStatus === DataType.ReviewStatusEnum.VERIFY.value) {
                $this.$emit('profileUpdated', ['statement'])
              }
            }
          }
          $this.storeSectionData(updateResult)
        })
      })
    }
    this.$eventBus.$on('broadcastLockedId', target => {
      this.currentLockedId = target
      const $this = this
      setTimeout(function () {
        const sectionName = $this.layoutSetting.section.name
        $this.calculateScore(sectionName)
      }, 2000)
    })
    if (this.layoutSetting.section.name === 'personal') {
      this.$eventBus.$on('broadcastOptIn', target => {
        this.uploadOptInData(target.optInData)
      })
    }
    if (this.layoutSetting.section.name === 'bank') {
      this.$eventBus.$on('uploadQuestion', target => {
        this.storeQuestionTemplate()
      })
    }
  },
  methods: {
    showTitle (loanType) {
      return this.loan.loanType === loanType
    },
    isShowElement (privileges, code, value) {
      return Authentication.matchElementPrivilege(privileges, code, value) && Authentication.isLockedByCurrentUser(this.store.getters.getAccount, this.currentLockedId)
    },
    isOperational () {
      for (const item of this.authorization.restricts) {
        if (Object.prototype.hasOwnProperty.call(item, 'loanStatus') && item.loanStatus === this.loan.loanStatus) {
          return true
        }
      }
      return false
    },
    isEdit () {
      const editArray = [
        DataType.LoanStatus.AGENT_REVIEW.value, DataType.LoanStatus.CORRECTOR_REVIEW.value
      ]
      return editArray.includes(this.loan.loanStatus)
    },
    isAudit () {
      const auditArray = [
        DataType.LoanStatus.UNDERWRITER_REVIEW.value, DataType.LoanStatus.TRIBE_REVIEW.value
      ]
      return auditArray.includes(this.loan.loanStatus)
    },
    calculateScore (sectionName) {
      const sectionData = this.score.sectionData
      const reviewData = this.score.reviewData
      if ((sectionName === 'statement' || this.assist.tool.isNotEmpty(sectionData)) &&
              this.assist.tool.isNotEmpty(reviewData)) {
        this.$eventBus.$emit('calculateScore', { sectionData, reviewData, sectionName })
      }
    },
    uploadOptInData (optInData) {
      const optInObject = {}
      for (const item of optInData) {
        if (item.caption === 'Email') {
          optInObject.emailIsMarketing = item.status.isMarketing
          optInObject.emailIsOperation = item.status.isOperation
          optInObject.email = optInObject.emailIsMarketing || optInObject.emailIsOperation
        } else if (item.caption === ' Telephone') {
          optInObject.phoneIsMarketing = item.status.isMarketing
          optInObject.phoneIsOperation = item.status.isOperation
          optInObject.phoneNotCall = item.status.doNotCall
          optInObject.phoneUncontactable = item.status.uncontactable
          optInObject.phone = optInObject.phoneIsMarketing || optInObject.phoneIsOperation || optInObject.phoneNotCall || optInObject.phoneUncontactable
        }
      }

      for (const item of this.optInArray) {
        item.display = optInObject[item.name]
      }
    },

    /* start of callback method */
    listQuestionTemplateCallback (result) {
      let questionTemplate = result
      if (this.assist.tool.isEmpty(questionTemplate)) {
        questionTemplate = []
      }
      this.dataSource.questionTemplateData = questionTemplate
    },
    getFullName (data) {
      const result = {}
      if (Object.prototype.hasOwnProperty.call(data, 'firstName') && tool.isNotEmpty(data.firstName)) {
        result.fullName = data.firstName.toUpperCase().substr(0, 1) + data.firstName.substr(1)
      }

      if (Object.prototype.hasOwnProperty.call(data, 'middleName') && tool.isNotEmpty(data.middleName)) {
        result.fullName = result.fullName + ' ' + data.middleName.toUpperCase().substr(0, 1) + data.middleName.substr(1)
      }

      if (Object.prototype.hasOwnProperty.call(data, 'lastName') && tool.isNotEmpty(data.lastName)) {
        result.fullName = result.fullName + ' ' + data.lastName.toUpperCase().substr(0, 1) + data.lastName.substr(1)
      }

      return result
    },
    storeQuestionTemplate () {
      const questionTemplateData = JSON.parse(JSON.stringify(this.dataSource.questionTemplateData))
      const data = this.getFullName(this.store.getters.getLoan)
      for (const item of questionTemplateData) {
        if (item.id === 4) {
          item.question = _.template(item.question)({ data: data })
        }
      }
      this.dataSource.questionTemplateData = questionTemplateData
      const questionOptionCache = {}
      for (const item of this.dataSource.questionTemplateData) {
        const optionArray = (item.options).toJson()
        const optionCache = {}
        for (const optionItem of optionArray) {
          optionCache[optionItem.value] = optionItem.text
        }
        questionOptionCache[item.id] = optionCache
      }
      this.extension.questionOptionData = questionOptionCache
    },
    storeAnswerData (answerData) {
      // TODO Delete this method
      this.storeQuestionTemplate()
      if (this.assist.tool.isEmpty(answerData)) {
        answerData = []
      }

      this.dataSource.answerData = answerData
      const questionAnswerData = {}
      if (answerData.length > 0) {
        for (const item of answerData) {
          questionAnswerData[item.question] = {
            value: item.answer,
            text: this.extension.questionOptionData[item.question][item.answer]
          }
        }
      } else {
        const questions = Object.keys(this.extension.questionOptionData)
        for (const question of questions) {
          questionAnswerData[question] = null
        }
      }
      this.extension.questionAnswerData = questionAnswerData
    },
    getApplicationQuestionDataCallback (result) {
      let answerData = result
      if (this.assist.tool.isEmpty(answerData)) {
        answerData = []
      }
      this.dataSource.answerData = answerData
    },
    async getLoanDetailsBasicCallback (result) {
      this.store.commit('setPPSNextAdvanceNumber', parseInt(result.advanceNo.split('-')[2]))
      await this.listLoanDetailsAdvance(result)
    },
    async listLoanDetailsAdvance (result) {
      let calData
      if (this.loan.loanType === DataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value) {
        this.$eventBus.$emit('get-latest-slave-detail-event', 'update loan history')
        // ProcessingApi.listLoanDetailsAdvance({ loanId: this.slaveLoanId, portfolioId: this.loan.portfolioId }, this.listLoanDetailsAdvanceCallBack)
        // mock
        await ProcessingApi.listLoanDetailsAdvance({ loanId: 20, portfolioId: this.loan.portfolioId }, function (res) {
          calData = {
            maximumAdvanceAmount: res.maximumAdvanceAmount,
            initialAdvanceAmount: res.initialAdvanceAmount,
            availableAmount: res.availableAmount,
            firstDisbursementDate: res.firstDisbursementDate
          }
          Object.assign(result, calData)
        })
        await this.storeSectionData(result)
        return
      }
      await this.storeSectionData(result)
    },
    async getApplicationFeatureDataCallback (result) {
      if (this.layoutSetting.section.name === 'payment-option' && tool.isNotEmpty(result)) {
        let loanId = this.loanId
        if (this.loan.loanType === DataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value) {
          loanId = this.slaveLoanId
        }
        await PaymentApi.getPaymentCalculateData(loanId, function (res) {
          const paymentCalculateData = {
            debitCount: res.debitCount,
            firstDebitDate: res.firstDebitDate,
            lastDebitDate: res.lastDebitDate,
            regularAmount: res.regularAmount,
            transferredAmount: res.transferredAmount,
            apr: res.apr
          }
          Object.assign(result, paymentCalculateData)
        })
        for (const key of Object.keys(result)) {
          if (result[key] === 0) {
            delete result[key]
          }
        }
      }
      this.storeSectionData(result)
      this.nextTickCallback()
    },
    getApplicationReviewDataCallback (result) {
      this.storeReviewData(result)
    },
    getSummaryByLoanIdCallBack (result) {
      const processingPaidFee = result.processingPaidFee
      const collectionPaidFee = result.collectionPaidFee
      const leftPaidFee = result.collectionLeftFee
      const sectionData = {
        inCollectedDay: result.inCollectedDay,
        collectedAt: result.collectedAt,
        processingPrincipal: processingPaidFee.principal,
        processingInterest: processingPaidFee.interest,
        processingNsfFee: processingPaidFee.nsfFee,
        processingLateFee: processingPaidFee.lateFee,
        totalProcessing: processingPaidFee.amount,
        collectionPrincipal: collectionPaidFee.principal,
        collectionInterest: collectionPaidFee.interest,
        collectionNsfFee: collectionPaidFee.nsfFee,
        collectionLateFee: collectionPaidFee.lateFee,
        totalCollection: collectionPaidFee.amount,
        leftPrincipal: leftPaidFee.principal,
        leftInterest: leftPaidFee.interest,
        leftNsfFee: leftPaidFee.nsfFee,
        leftLateFee: leftPaidFee.lateFee,
        totalLeft: leftPaidFee.amount
      }
      this.storeSectionData(sectionData)
    },
    listProgramDataCallback (result) {
      this.store.commit('setProgram', result)
      this.storeProgramData(result)
    },
    /* end of callback method //:~ */

    /* start of render method */
    nextTickCallback () {
      this.$nextTick(() => {
        if (tool.isNotNull(this.$refs.profileSection)) {
          this.$emit('calcHeight', this.$refs.profileSection.offsetHeight, this.layoutSetting.section.name)
        }
      })
    },
    /* end of render method //:~ */

    /* start of operation method */
    showProfileSectionDialog (action) {
      this.dialog.profileSectionDialog.action = action
      this.dialog.profileSectionDialog.display = true
    },
    storeSectionData (sectionData) {
      switch (this.layoutSetting.section.type) {
        case 'form':
          if (this.assist.tool.isEmpty(sectionData)) {
            sectionData = {}
          }
          if (this.loan.loanType === DataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value &&
            this.layoutSetting.section.name === 'payment-option' &&
            this.loan.phase === DataType.LoanPhaseEnum.LOAN_ORIGINATION.value
          ) {
            sectionData.advanceNo = 1
            sectionData.stage = 'S1'
          }
          this.dataSource.sectionData = Object.assign(this.dataSource.sectionData, sectionData)
          break
        case 'table':
          if (this.assist.tool.isEmpty(sectionData)) {
            sectionData = []
          }

          for (const item of sectionData) {
            if (item.payrollDate < 0 || !item.payrollDate.toString().includes('-')) {
              item.payrollDate = tool.formatDate(item.payrollDate)
            }
          }
          this.dataSource.tableData = sectionData
          break
        default:
          break
      }

      this.store.commit('setLoan', { loan: sectionData, sectionName: this.layoutSetting.section.name })
      if (this.layoutSetting.section.name === 'personal') {
        this.$eventBus.$emit('uploadQuestion')
      }
    },
    storeReviewData (sourceData) {
      if (this.assist.tool.isEmpty(sourceData)) {
        sourceData = []
      }

      const targetData = []
      const reviewStatusData = {}

      if (sourceData.length > 0) {
        const sectionKey = this.layoutSetting.section.name

        for (const item of sourceData) {
          if (item.sectionKey === sectionKey) {
            reviewStatusData[item.fieldKey] = item.reviewStatus

            targetData.push(item)
          }
        }
      }

      this.dataSource.reviewData = targetData
      this.extension.reviewStatusData = reviewStatusData
    },
    storeProgramData (programArray) {
      this.dataSource.programArray = programArray
      this.extension.programData = []

      for (const program of programArray) {
        this.extension.programData.push({
          value: program.id,
          text: program.programName
        })
      }
    },
    /* end of operation method //:~ */

    /* start of event method */
    toLoadSectionData (loanId) {
      this.loanIdWatcher(loanId)
    },
    async loanIdWatcher (loanId) {
      const feature = this.layoutSetting.section.name

      if (this.assist.tool.isNotEmpty(this.layoutSetting.metadata)) {
        for (const metadata of this.layoutSetting.metadata) {
          switch (metadata.name) {
            case 'program':
              // MaintenanceApi.listProgramData(this.loan.portfolioId, this.listProgramDataCallback)
              break
            case 'questionType':
              this.dataSource.answerData = []
              this.dataSource.questionTemplateData = AnalogData.QUESTION
              // await MaintenanceApi.listQuestionTemplate(metadata.value, this.listQuestionTemplateCallback)
              await OriginationApi.listLoanQuestionData(loanId, this.storeAnswerData)
              break
            default:
              break
          }
        }
      }
      if (feature === 'statement') {
        OriginationApi.getLoanFeatureData(loanId, 'list', feature, this.getApplicationFeatureDataCallback)
      } else if (this.loan.phase === DataType.LoanPhaseEnum.PAYMENT_PROCESSING.value && feature === 'payment-option') {
        this.loan.loanType === DataType.LoanTypeEnum.INSTALLMENT_LOAN.value && this.$eventBus.$emit('get-clicked-slave-detail-event', { loanId: loanId, isFirstLoad: true })
        await ProcessingApi.getLoanDetailsBasic({ loanId: 20 }, this.getLoanDetailsBasicCallback)
      } else {
        OriginationApi.getLoanFeatureData(loanId, 'get', feature, this.getApplicationFeatureDataCallback)
      }
      OriginationApi.listLoanReviewDataRelatedAssignedLoan(loanId, feature, this.getApplicationReviewDataCallback)
    },
    loadSlaveLoanPaymentOption (slaveLoanId) {
      const feature = this.layoutSetting.section.name
      if (feature === 'payment-option' &&
        this.loan.loanType === DataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value &&
        this.loan.phase === DataType.LoanPhaseEnum.LOAN_ORIGINATION.value &&
        tool.isNotEmpty(slaveLoanId)) {
        OriginationApi.getLoanFeatureData(slaveLoanId, 'get', feature, this.getApplicationFeatureDataCallback)
      }
    },
    sectionUpdatedEventHandler (newAppData, action, updatedKeys) {
      const reviewData = newAppData.reviewData
      const sectionData = newAppData.sectionData
      const tableData = newAppData.tableData
      const answerData = newAppData.answerData

      if (this.assist.tool.isNotEmpty(reviewData)) {
        this.storeReviewData(reviewData)
      }

      const originalTableData = JSON.parse(JSON.stringify(this.dataSource.tableData))
      originalTableData.sort(this.assist.tool.compareArr('date'))
      tableData.sort(this.assist.tool.compareArr('date'))
      if (action !== 'AUDIT' && Crypto.md5(JSON.stringify(originalTableData)) !== Crypto.md5(JSON.stringify(tableData))) {
        this.storeSectionData(tableData)
      }

      if (this.assist.tool.isNotEmpty(answerData)) {
        this.storeAnswerData(answerData)
      }

      if (action !== 'AUDIT' && this.assist.tool.isNotEmpty(sectionData)) {
        this.storeSectionData(sectionData)
      }

      if (this.assist.tool.isNotEmpty(updatedKeys) && updatedKeys.length > 0) {
        this.$emit('profileUpdated', updatedKeys)
      }
    },
    hidePaymentPlanEventHandler () {
      this.dialog.paymentPlanDialog.display = false
    },
    /* end of event method //:~ */

    /* ******************************************** start of original ********************************************* */
    triggerAmount (action) {
      switch (action) {
        case 'triggerRecalculateLoanAmount':
          this.triggerRecalculateLoanAmount()
          break
        case 'triggerAdjustApprovedAmount':
          this.triggerAdjustApprovedAmount()
          break
        default:
          break
      }
    },
    triggerRecalculateLoanAmount () {
      this.$eventBus.$emit('calculateLoanSize')
    },
    triggerAdjustApprovedAmount () {
      const sectionData = this.dataSource.sectionData
      const isShowDialog = tool.isNotEmpty(sectionData) && tool.isNotEmpty(sectionData.loanAmount) && sectionData.loanAmount > 0
      if (isShowDialog) {
        this.dialog.paymentPlanDialog.display = isShowDialog
      } else {
        popup.popupForWarning('The customer has no available loan amount')
      }
    }
    /* ******************************************** end of original ********************************************* */
  }
}
</script>

<style lang="scss" scoped>
.profile-section {
  background: #F5F5F5;
  padding: 20px;
  margin-top: 0 !important;
  .section-header {
    margin-bottom: -15px;
    padding-top: 12px;
    height: 36px;
    .section-name {
      font-weight: 600;
      font-size: 12px;
      text-transform: uppercase;
      display: inline-block;
    }
    .section-action {
      text-align: right;
      font-size: 20px;
    }
  }
  .section-banner {
    margin: 10px 0;
    font-size: 20px;
    .row {
      margin: auto !important;
    }
  }
  .section-table {
    margin-top: 10px;
  }
  .section-header .flex, .section-banner .flex {
    padding: 0 12px !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
